import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import SubcontractReport from './SubcontractReport'

/**
 * SubcontractReportControls.jsx
 *
 * Dedicated Report Controls flow, per the approved spec's Section 10
 * ("prefer a dedicated Report Controls / Report Preview flow" over
 * cluttering the main Subcontracts page). Loads its own lightweight
 * contract list (id/contract_no/contractor_name/status/contract_type)
 * — NOT the full Subcontracts.jsx dataset — purely to populate the
 * Contractor multi-select and Select All/Clear All behavior.
 *
 * Renders the actual report via SubcontractReport.jsx, passing the
 * selected scope as props — SubcontractReport.jsx itself does the real
 * data fetching for the report content once scope is confirmed.
 */
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
]
const DEFAULT_STATUSES = ['approved', 'closed']

const SECTION_OPTIONS = [
  { key: 'executiveSummary', label: 'Executive Summary' },
  { key: 'contractSummary', label: 'Contract Summary' },
  { key: 'variations', label: 'Variations' },
  { key: 'financialPosition', label: 'Financial Position' },
  { key: 'retention', label: 'Retention' },
  { key: 'contractorDetails', label: 'Contractor Details' },
]
const DEFAULT_SECTIONS = SECTION_OPTIONS.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})

export default function SubcontractReportControls({ projectId, onClose }) {
  const [loading, setLoading] = useState(true)
  const [allContracts, setAllContracts] = useState([]) // { id, contract_no, contractor_name, status, contract_type }
  const [search, setSearch] = useState('')

  const [contractTypes, setContractTypes] = useState({ material: true, equipment: true })
  const [statuses, setStatuses] = useState(new Set(DEFAULT_STATUSES))
  const [selectedContractorIds, setSelectedContractorIds] = useState(new Set())
  const [sections, setSections] = useState(DEFAULT_SECTIONS)

  const [showReport, setShowReport] = useState(false)

  useEffect(() => { if (projectId) loadContracts(projectId) }, [projectId])

  async function loadContracts(pid) {
    setLoading(true)
    const [{ data: subs }, { data: eqSubs }] = await Promise.all([
      supabase.from('subcontracts').select('id, contract_no, contractor_name, status').eq('project_id', pid),
      supabase.from('equipment_contracts').select('id, contract_no, contractor_name, status').eq('project_id', pid),
    ])
    setAllContracts([
      ...(subs || []).map(c => ({ ...c, contract_type: 'material' })),
      ...(eqSubs || []).map(c => ({ ...c, contract_type: 'equipment' })),
    ])
    setLoading(false)
  }

  const typeFilteredContracts = useMemo(() =>
    allContracts.filter(c => contractTypes[c.contract_type]),
    [allContracts, contractTypes]
  )

  const statusFilteredContracts = useMemo(() =>
    typeFilteredContracts.filter(c => statuses.has(c.status || 'draft')),
    [typeFilteredContracts, statuses]
  )

  const searchedContracts = useMemo(() => {
    if (!search.trim()) return statusFilteredContracts
    const q = search.toLowerCase()
    return statusFilteredContracts.filter(c =>
      (c.contractor_name || '').toLowerCase().includes(q) || (c.contract_no || '').toLowerCase().includes(q)
    )
  }, [statusFilteredContracts, search])

  function toggleContractor(id) {
    setSelectedContractorIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function selectAll() {
    setSelectedContractorIds(new Set(searchedContracts.map(c => c.id)))
  }
  function clearAll() {
    setSelectedContractorIds(new Set())
  }
  function toggleSection(key) {
    setSections(prev => ({ ...prev, [key]: !prev[key] }))
  }
  function toggleStatus(value) {
    setStatuses(prev => {
      const next = new Set(prev)
      next.has(value) ? next.delete(value) : next.add(value)
      return next
    })
  }

  if (showReport) {
    return (
      <SubcontractReport
        projectId={projectId}
        contractTypes={Object.entries(contractTypes).filter(([, v]) => v).map(([k]) => k)}
        statuses={[...statuses]}
        contractorIds={selectedContractorIds.size > 0 ? [...selectedContractorIds] : null}
        sections={sections}
        onClose={() => setShowReport(false)}
      />
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.6)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800 }}>Subcontract Report — Controls</h2>
          <button onClick={onClose} style={{ padding: '6px 14px', fontSize: 12, borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>✕ Close</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading contracts…</div>
        ) : (
          <>
            <Field label="Contract Type">
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={contractTypes.material} onChange={() => setContractTypes(t => ({ ...t, material: !t.material }))} />
                  📦 Material
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={contractTypes.equipment} onChange={() => setContractTypes(t => ({ ...t, equipment: !t.equipment }))} />
                  🚜 Equipment
                </label>
              </div>
            </Field>

            <Field label="Status">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {STATUS_OPTIONS.map(s => (
                  <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={statuses.has(s.value)} onChange={() => toggleStatus(s.value)} />
                    {s.label}
                  </label>
                ))}
              </div>
            </Field>

            <Field label={`Contractor (${selectedContractorIds.size > 0 ? selectedContractorIds.size + ' selected' : 'all matching type/status'})`}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search contractor or contract no…"
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12.5 }}
                />
                <button onClick={selectAll} style={miniButtonStyle}>Select All</button>
                <button onClick={clearAll} style={miniButtonStyle}>Clear All</button>
              </div>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, padding: 8 }}>
                {searchedContracts.length === 0 && <div style={{ fontSize: 12, color: '#9CA3AF', padding: 8 }}>No contracts match the current Type/Status filters.</div>}
                {searchedContracts.map(c => (
                  <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 4px' }}>
                    <input type="checkbox" checked={selectedContractorIds.has(c.id)} onChange={() => toggleContractor(c.id)} />
                    <span>{c.contract_type === 'equipment' ? '🚜' : '📦'} {c.contractor_name} <span style={{ color: '#9CA3AF' }}>({c.contract_no})</span></span>
                  </label>
                ))}
              </div>
            </Field>

            <Field label="Report Sections">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {SECTION_OPTIONS.map(s => (
                  <label key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={sections[s.key]} onChange={() => toggleSection(s.key)} />
                    {s.label}
                  </label>
                ))}
              </div>
            </Field>

            <button
              onClick={() => setShowReport(true)}
              disabled={!contractTypes.material && !contractTypes.equipment}
              style={{ width: '100%', padding: 12, background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 8, opacity: (!contractTypes.material && !contractTypes.equipment) ? 0.5 : 1 }}
            >
              Generate / Preview Report
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</label>
      {children}
    </div>
  )
}

const miniButtonStyle = { padding: '6px 12px', fontSize: 11.5, borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap' }
